import { useCallback, useRef, useState } from 'react';
import type { Edge, Node } from '@xyflow/react';
import { useReactFlow } from '@xyflow/react';
import { isAncestorOf } from './orgChartUtils';
import { organizationalUnitsApi } from '../../services/apiService';
import { useNotification } from '../../contexts/NotificationContext';

const DROP_PROXIMITY_THRESHOLD = 250;
const CHILD_ZONE_OFFSET = 30; // pixels below target center = "make child" zone

export type DropMode = 'child' | 'sibling';

interface DropInfo {
  targetId: string;
  mode: DropMode;
}

export interface PendingMove {
  draggedId: number;
  newParentId: number | null;
  previousParentId: number | null;
}

interface UseOrgChartDragDropOptions {
  edges: Edge[];
  refreshTree: () => Promise<void>;
  reactFlowInstance: ReturnType<typeof useReactFlow> | null;
  onMoveComplete?: () => void;
  onMoveCountChange?: (count: number) => void;
}

function findClosestNodeWithMode(
  draggedNode: Node,
  allNodes: Node[],
): DropInfo | null {
  let closest: Node | null = null;
  let closestDist = Infinity;
  const dx = draggedNode.position.x + 100;
  const dy = draggedNode.position.y + 35;

  for (const node of allNodes) {
    if (node.id === draggedNode.id || node.id === '-1') continue;
    const nx = node.position.x + 100;
    const ny = node.position.y + 35;
    const dist = Math.sqrt((dx - nx) ** 2 + (dy - ny) ** 2);
    if (dist < closestDist) {
      closestDist = dist;
      closest = node;
    }
  }

  if (!closest || closestDist > DROP_PROXIMITY_THRESHOLD) return null;

  const targetCenterY = closest.position.y + 35;
  const mode: DropMode = dy > targetCenterY + CHILD_ZONE_OFFSET ? 'child' : 'sibling';

  return { targetId: closest.id, mode };
}

function getParentId(nodeId: string, edges: Edge[]): string | null {
  const parentEdge = edges.find((e) => e.target === nodeId);
  return parentEdge?.source ?? null;
}

export function useOrgChartDragDrop({ edges, refreshTree, reactFlowInstance, onMoveComplete, onMoveCountChange }: UseOrgChartDragDropOptions) {
  const { showError, showSuccess } = useNotification();
  const isMovingRef = useRef(false);
  const [dropTargetId, setDropTargetId] = useState<string | null>(null);
  const [dropMode, setDropMode] = useState<DropMode | null>(null);
  // Track completed moves for undo on cancel
  const completedMovesRef = useRef<PendingMove[]>([]);

  const onNodeDragStart = useCallback((_event: React.MouseEvent, _node: Node) => {
    // noop
  }, []);

  const onNodeDrag = useCallback(
    (_event: React.MouseEvent, draggedNode: Node) => {
      if (!reactFlowInstance || draggedNode.id === '-1') return;
      const allNodes = reactFlowInstance.getNodes();
      const info = findClosestNodeWithMode(draggedNode, allNodes);
      setDropTargetId(info?.targetId ?? null);
      setDropMode(info?.mode ?? null);
    },
    [reactFlowInstance],
  );

  const onNodeDragStop = useCallback(
    async (_event: React.MouseEvent, draggedNode: Node) => {
      setDropTargetId(null);
      setDropMode(null);

      if (!reactFlowInstance || isMovingRef.current) return;

      if (draggedNode.id === '-1') {
        refreshTree();
        return;
      }

      const allNodes = reactFlowInstance.getNodes();
      const intersections = reactFlowInstance.getIntersectingNodes(draggedNode);
      const intersectedTarget = intersections.find((n) => n.id !== draggedNode.id && n.id !== '-1');

      let dropInfo: DropInfo | null = null;

      if (intersectedTarget) {
        const dy = draggedNode.position.y + 35;
        const targetCenterY = intersectedTarget.position.y + 35;
        const mode: DropMode = dy > targetCenterY + CHILD_ZONE_OFFSET ? 'child' : 'sibling';
        dropInfo = { targetId: intersectedTarget.id, mode };
      } else {
        dropInfo = findClosestNodeWithMode(draggedNode, allNodes);
      }

      if (!dropInfo) {
        refreshTree();
        return;
      }

      const draggedId = draggedNode.id;
      let newParentId: number | null;

      if (dropInfo.mode === 'child') {
        newParentId = Number(dropInfo.targetId);
      } else {
        const targetParentId = getParentId(dropInfo.targetId, edges);
        if (targetParentId === '-1' || targetParentId === null) {
          newParentId = null;
        } else {
          newParentId = Number(targetParentId);
        }
      }

      // Already parented here — skip
      const currentParentEdge = edges.find((e) => e.target === draggedId);
      const currentParentId = currentParentEdge?.source ?? null;
      const currentParentNum = currentParentId === '-1' || currentParentId === null ? null : Number(currentParentId);
      if (newParentId === currentParentNum) {
        refreshTree();
        return;
      }

      // Circular reference check
      if (dropInfo.mode === 'child' && isAncestorOf(draggedId, dropInfo.targetId, edges)) {
        showError('لا يمكن نقل وحدة تحت وحدة تابعة لها');
        refreshTree();
        return;
      }

      // Save immediately and track for undo
      try {
        isMovingRef.current = true;
        await organizationalUnitsApi.move(Number(draggedId), {
          new_parent_id: newParentId,
          position: 0,
        });
        // Track this move for potential undo
        completedMovesRef.current.push({
          draggedId: Number(draggedId),
          newParentId,
          previousParentId: currentParentNum,
        });
        onMoveCountChange?.(completedMovesRef.current.length);
        showSuccess('تم نقل الوحدة');
        await refreshTree();
        onMoveComplete?.();
      } catch (err: unknown) {
        let message = 'فشل في نقل الوحدة';
        if (err && typeof err === 'object') {
          const axiosErr = err as { response?: { data?: { detail?: string } }; message?: string };
          if (axiosErr.response?.data?.detail) {
            message = axiosErr.response.data.detail;
          } else if (axiosErr.message) {
            message = axiosErr.message;
          }
        }
        showError(message);
        await refreshTree();
      } finally {
        isMovingRef.current = false;
      }
    },
    [edges, refreshTree, reactFlowInstance, showError, showSuccess, onMoveComplete, onMoveCountChange],
  );

  // "Save" = just clear the undo history (moves are already saved)
  const confirmMoves = useCallback(() => {
    completedMovesRef.current = [];
    onMoveCountChange?.(0);
  }, [onMoveCountChange]);

  // "Cancel" = undo all moves in reverse order
  const undoAllMoves = useCallback(async () => {
    const moves = [...completedMovesRef.current].reverse();
    if (moves.length === 0) return;

    try {
      isMovingRef.current = true;
      for (const move of moves) {
        await organizationalUnitsApi.move(move.draggedId, {
          new_parent_id: move.previousParentId,
          position: 0,
        });
      }
      showSuccess('تم التراجع عن التغييرات');
      completedMovesRef.current = [];
      onMoveCountChange?.(0);
      await refreshTree();
      onMoveComplete?.();
    } catch (err: unknown) {
      let message = 'فشل في التراجع عن التغييرات';
      if (err && typeof err === 'object') {
        const axiosErr = err as { response?: { data?: { detail?: string } }; message?: string };
        if (axiosErr.response?.data?.detail) {
          message = axiosErr.response.data.detail;
        } else if (axiosErr.message) {
          message = axiosErr.message;
        }
      }
      showError(message);
      await refreshTree();
    } finally {
      isMovingRef.current = false;
    }
  }, [refreshTree, showError, showSuccess, onMoveComplete, onMoveCountChange]);

  return { onNodeDragStart, onNodeDrag, onNodeDragStop, dropTargetId, dropMode, confirmMoves, undoAllMoves };
}
